  import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import {
  fetchSellingTypes,
  fetchWirePrices,
  fetchSellingPrices,
  updateWirePrice,
  updateSellingPrice,
  createSellingPrice,
  createSellingType,
  createWirePrice,
  updateSellingType,
  deleteSellingType,
  deleteWirePrice,
  deleteSellingPrice,
  fetchBudgetCalculationPrices,
  updateBudgetCalculationPrice,
} from '@/services/products'
import type { SellingType, WirePrice, SellingPrice, BudgetCalculationPrice } from '@/types/products'
import { AlertCircle, Plus, Trash2 } from 'lucide-react'

interface EditableCellProps {
  value: number
  onSave: (newValue: number) => Promise<void>
  formatValue?: (value: number) => string
  prefix?: string
  className?: string
  title?: string
}

function EditableCell({
  value,
  onSave,
  formatValue = (v) => v.toString(),
  prefix = '',
  className = '',
  title = '',
}: EditableCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDoubleClick = useCallback(() => {
    setEditValue(value.toString())
    setIsEditing(true)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    const newValue = parseFloat(editValue)
    if (isNaN(newValue) || newValue === value) {
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await onSave(newValue)
      setIsEditing(false)
    } catch {
      // Keep editing on error
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type='number'
        step='0.01'
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        className='h-6 w-20 px-1 py-0 text-right text-xs'
      />
    )
  }

  return (
    <span
      onDoubleClick={handleDoubleClick}
      className={`cursor-pointer rounded px-1 hover:bg-primary/10 ${className}`}
      title={title}
    >
      {prefix}
      {formatValue(value)}
    </span>
  )
}

interface EditableEmptyCellProps {
  onCreate: (profit: number) => Promise<void>
  className?: string
  title?: string
}

function EditableEmptyCell({ onCreate, className = '', title = '' }: EditableEmptyCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDoubleClick = useCallback(() => {
    setEditValue('')
    setIsEditing(true)
  }, [])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isEditing])

  const handleSave = async () => {
    const profit = parseFloat(editValue)
    if (isNaN(profit)) {
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await onCreate(profit)
      setIsEditing(false)
    } catch {
      // Keep editing on error
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type='number'
        step='0.01'
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        placeholder='0.00'
        className='h-6 w-20 px-1 py-0 text-right text-xs'
      />
    )
  }

  return (
    <span
      onDoubleClick={handleDoubleClick}
      className={`cursor-pointer rounded px-1 hover:bg-primary/10 ${className}`}
      title={title}
    >
      -
    </span>
  )
}

interface EditableTextCellProps {
  value: string
  onSave: (newValue: string) => Promise<void>
  className?: string
  title?: string
}

function EditableTextCell({
  value,
  onSave,
  className = '',
  title = '',
}: EditableTextCellProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDoubleClick = useCallback(() => {
    setEditValue(value)
    setIsEditing(true)
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    const trimmed = editValue.trim()
    if (!trimmed || trimmed === value) {
      setIsEditing(false)
      return
    }
    setIsSaving(true)
    try {
      await onSave(trimmed)
      setIsEditing(false)
    } catch {
      // Keep editing on error
    } finally {
      setIsSaving(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type='text'
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        disabled={isSaving}
        className='h-6 w-24 px-1 py-0 text-center text-xs'
      />
    )
  }

  return (
    <span
      onDoubleClick={handleDoubleClick}
      className={`cursor-pointer rounded px-1 hover:bg-primary/10 ${className}`}
      title={title}
    >
      {value}
    </span>
  )
}

function LoadingSkeleton() {
  return (
    <div className='space-y-2'>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} className='h-12 w-full' />
      ))}
    </div>
  )
}

export default function Pricing() {
  const { t } = useTranslation()
  const [sellingTypes, setSellingTypes] = useState<SellingType[]>([])
  const [wirePrices, setWirePrices] = useState<WirePrice[]>([])
  const [sellingPrices, setSellingPrices] = useState<SellingPrice[]>([])
  const [budgetCalcPrices, setBudgetCalcPrices] = useState<BudgetCalculationPrice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [newTypeName, setNewTypeName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // Wire price creation state
  const [isWireSheetOpen, setIsWireSheetOpen] = useState(false)
  const [isCreatingWire, setIsCreatingWire] = useState(false)
  const [newWirePrice, setNewWirePrice] = useState({
    name: '',
    square: '',
    thickness_mm: '',
    price: '',
  })

  // Custom context menu state for wire price rows
  const [contextMenu, setContextMenu] = useState<{
    x: number
    y: number
    wirePrice: WirePrice | null
  } | null>(null)

  // Custom context menu state for selling price rows
  const [sellingPriceContextMenu, setSellingPriceContextMenu] = useState<{
    x: number
    y: number
    sellingPrice: SellingPrice | null
  } | null>(null)

  const handleDeleteSellingType = async (st: SellingType) => {
    try {
      await deleteSellingType(st.id)
      setSellingTypes((prev) => prev.filter((s) => s.id !== st.id))
      setSellingPrices((prev) =>
        prev.filter((sp) => sp.selling_type.id !== st.id)
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete selling type')
    }
  }

  const handleDeleteWirePrice = async (wp: WirePrice) => {
    try {
      await deleteWirePrice(wp.id)
      setWirePrices((prev) => prev.filter((w) => w.id !== wp.id))
      setSellingPrices((prev) =>
        prev.filter((sp) => sp.wire_price.id !== wp.id)
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete wire price')
    }
  }

  const handleDeleteSellingPrice = async (sp: SellingPrice) => {
    try {
      await deleteSellingPrice(sp.id)
      setSellingPrices((prev) => prev.filter((p) => p.id !== sp.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete selling price')
    }
  }

  const handleCreateSellingType = async () => {
    if (!newTypeName.trim()) return
    setIsCreating(true)
    try {
      const created = await createSellingType({ name: newTypeName.trim() })
      setSellingTypes((prev) => [...prev, created])
      setNewTypeName('')
      setIsSheetOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create selling type')
    } finally {
      setIsCreating(false)
    }
  }

  const handleCreateWirePrice = async () => {
    const { name, square, thickness_mm, price } = newWirePrice
    if (!name.trim() || !square || !thickness_mm || !price) return
    setIsCreatingWire(true)
    try {
      const created = await createWirePrice({
        name: name.trim(),
        square: parseFloat(square),
        thickness_mm: parseFloat(thickness_mm),
        price: parseFloat(price),
      })
      // Insert new wire price in sorted position by square (descending), then thickness (ascending)
      setWirePrices((prev) => {
        const updated = [...prev, created]
        return updated.sort((a, b) => {
          if (b.square !== a.square) {
            return b.square - a.square
          }
          return a.thickness_mm - b.thickness_mm
        })
      })
      setNewWirePrice({ name: '', square: '', thickness_mm: '', price: '' })
      setIsWireSheetOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create wire price')
    } finally {
      setIsCreatingWire(false)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true)
        const [types, wires, prices, budgetPrices] = await Promise.all([
          fetchSellingTypes(),
          fetchWirePrices(),
          fetchSellingPrices(),
          fetchBudgetCalculationPrices(),
        ])
        // Sort wire prices by square (katak) descending, then by thickness ascending
        const sortedWires = [...wires].sort((a, b) => {
          if (b.square !== a.square) {
            return b.square - a.square
          }
          return a.thickness_mm - b.thickness_mm
        })
        setSellingTypes(types)
        setWirePrices(sortedWires)
        setSellingPrices(prices)
        setBudgetCalcPrices(budgetPrices)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data')
      } finally {
        setIsLoading(false)
      }
    }
    loadData()
  }, [])

  // Close context menus on click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null)
      setSellingPriceContextMenu(null)
    }
    window.addEventListener('click', handleClick)
    return () => window.removeEventListener('click', handleClick)
  }, [])

  return (
    <>
      <header className='flex h-16 shrink-0 items-center justify-between gap-2 px-4'>
        <div className='flex items-center gap-2'>
          <SidebarTrigger className='-ml-1' />
          <Separator
            orientation='vertical'
            className='mr-2 data-[orientation=vertical]:h-4'
          />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>{t('pricing.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <Button size='sm' onClick={() => setIsSheetOpen(true)}>
          <Plus className='mr-1 h-4 w-4' />
          {t('pricing.addSellType')}
        </Button>
      </header>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('pricing.createSellingType')}</SheetTitle>
          </SheetHeader>
          <div className='p-4'>
            <Label htmlFor='typeName'>{t('common.name')}</Label>
            <Input
              id='typeName'
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder={t('pricing.createSellingType')}
              className='mt-2'
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSellingType()
              }}
            />
          </div>
          <SheetFooter>
            <Button
              onClick={handleCreateSellingType}
              disabled={isCreating || !newTypeName.trim()}
            >
              {isCreating ? t('common.loading') : t('common.create')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={isWireSheetOpen} onOpenChange={setIsWireSheetOpen}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('pricing.createWirePrice')}</SheetTitle>
          </SheetHeader>
          <div className='space-y-4 p-4'>
            <div>
              <Label htmlFor='wireName'>{t('pricing.wireName')}</Label>
              <Input
                id='wireName'
                value={newWirePrice.name}
                onChange={(e) =>
                  setNewWirePrice((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder='2.0'
                className='mt-1'
              />
            </div>
            <div>
              <Label htmlFor='wireSquare'>{t('pricing.wireSquare')}</Label>
              <Input
                id='wireSquare'
                type='number'
                value={newWirePrice.square}
                onChange={(e) =>
                  setNewWirePrice((prev) => ({ ...prev, square: e.target.value }))
                }
                placeholder='50'
                className='mt-1'
              />
            </div>
            <div>
              <Label htmlFor='wireThickness'>{t('pricing.wireThickness')}</Label>
              <Input
                id='wireThickness'
                type='number'
                step='0.01'
                value={newWirePrice.thickness_mm}
                onChange={(e) =>
                  setNewWirePrice((prev) => ({
                    ...prev,
                    thickness_mm: e.target.value,
                  }))
                }
                placeholder='1.95'
                className='mt-1'
              />
            </div>
            <div>
              <Label htmlFor='wirePrice'>{t('pricing.wirePrice')}</Label>
              <Input
                id='wirePrice'
                type='number'
                step='0.01'
                value={newWirePrice.price}
                onChange={(e) =>
                  setNewWirePrice((prev) => ({ ...prev, price: e.target.value }))
                }
                placeholder='50.00'
                className='mt-1'
              />
            </div>
          </div>
          <SheetFooter>
            <Button
              onClick={handleCreateWirePrice}
              disabled={
                isCreatingWire ||
                !newWirePrice.name.trim() ||
                !newWirePrice.square ||
                !newWirePrice.thickness_mm ||
                !newWirePrice.price
              }
            >
              {isCreatingWire ? t('common.loading') : t('common.create')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Custom Context Menu for Wire Price Rows */}
      {contextMenu && contextMenu.wirePrice && (
        <div
          className='fixed z-50 min-w-[180px] overflow-hidden rounded-md border bg-popover p-1 shadow-md'
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className='flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent hover:text-accent-foreground'
            onClick={() => {
              handleDeleteWirePrice(contextMenu.wirePrice!)
              setContextMenu(null)
            }}
          >
            <Trash2 className='h-4 w-4' />
            {t('pricing.delete')} "{contextMenu.wirePrice.name}" ({contextMenu.wirePrice.square})
          </button>
        </div>
      )}

      {/* Custom Context Menu for Selling Price Rows */}
      {sellingPriceContextMenu && sellingPriceContextMenu.sellingPrice && (
        <div
          className='fixed z-50 min-w-[180px] overflow-hidden rounded-md border bg-popover p-1 shadow-md'
          style={{ left: sellingPriceContextMenu.x, top: sellingPriceContextMenu.y }}
        >
          <button
            className='flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent hover:text-accent-foreground'
            onClick={(e) => {
              e.stopPropagation()
              handleDeleteSellingPrice(sellingPriceContextMenu.sellingPrice!)
              setSellingPriceContextMenu(null)
            }}
          >
            <Trash2 className='h-4 w-4' />
            {t('pricing.delete')} "{sellingPriceContextMenu.sellingPrice.selling_type.name}" ({sellingPriceContextMenu.sellingPrice.wire_price.square})
          </button>
        </div>
      )}

      {error && (
        <div className='mx-4 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400'>
          <AlertCircle className='h-4 w-4' />
          <span>{error}</span>
          <Button
            variant='ghost'
            size='sm'
            onClick={() => setError(null)}
            className='ml-auto'
          >
            {t('common.dismiss')}
          </Button>
        </div>
      )}

      {/* Budget Calculation Prices */}
      {!isLoading && budgetCalcPrices.length > 0 && (
        <div className='mx-4 mb-4 flex flex-wrap gap-6 rounded-lg border bg-card p-4 text-card-foreground shadow-sm'>
          <div className='text-sm font-semibold uppercase tracking-wide text-muted-foreground'>
            {t('pricing.budgetCalcPrices')}
          </div>
          {budgetCalcPrices.slice().sort((a, b) => b.id - a.id).map((item) => (
            <div key={item.id} className='flex items-center gap-2'>
              <span className='text-sm font-medium capitalize'>
                {t(`pricing.${item.price_type}`)}:
              </span>
              <EditableCell
                value={item.price}
                onSave={async (newPrice) => {
                  const updated = await updateBudgetCalculationPrice(item.id, {
                    price: newPrice,
                  })
                  setBudgetCalcPrices((prev) =>
                    prev.map((p) => (p.id === item.id ? updated : p))
                  )
                }}
                formatValue={(v) => '$' + v}
              />
            </div>
          ))}
        </div>
      )}

      <div className='flex flex-1 flex-col gap-4 p-4 pt-0'>
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <div className='overflow-x-auto'>
            <div className='rounded-lg border text-xs'>
              <table className='w-full border-collapse'>
                <thead className='sticky top-0 z-10'>
                  <tr className='border-b border-border bg-muted'>
                    <th
                      colSpan={6}
                      className='border-r-2 border-border px-1.5 py-2 text-left text-xs font-bold'
                    >
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setIsWireSheetOpen(true)}
                        className='h-6 text-xs'
                      >
                        <Plus className='mr-1 h-3 w-3' />
                        {t('pricing.addWirePrice')}
                      </Button>
                    </th>
                    {sellingTypes.map((st, idx) => (
                      <th
                        key={st.id}
                        colSpan={4}
                        className={`px-1.5 py-2 text-center text-xs font-bold uppercase tracking-wider ${
                          idx % 2 === 0
                            ? 'bg-blue-50 dark:bg-blue-950/30'
                            : 'bg-green-50 dark:bg-green-950/30'
                        } border-r border-border`}
                      >
                        <ContextMenu>
                          <ContextMenuTrigger>
                            <EditableTextCell
                              value={st.name}
                              onSave={async (newName) => {
                                const updated = await updateSellingType(st.id, {
                                  name: newName,
                                })
                                setSellingTypes((prev) =>
                                  prev.map((s) => (s.id === st.id ? updated : s))
                                )
                              }}
                            />
                          </ContextMenuTrigger>
                          <ContextMenuContent>
                            <ContextMenuItem
                              variant='destructive'
                              onClick={() => handleDeleteSellingType(st)}
                            >
                              <Trash2 className='h-4 w-4' />
                              {t('pricing.delete')} "{st.name}"
                            </ContextMenuItem>
                          </ContextMenuContent>
                        </ContextMenu>
                      </th>
                    ))}
                  </tr>
                  <tr className='border-b border-border bg-muted/70'>
                    <th className='whitespace-nowrap px-1.5 py-1 text-left font-semibold'>
                      {t('pricing.katak')}
                    </th>
                    <th className='whitespace-nowrap px-1.5 py-1 text-right font-semibold'>
                      {t('pricing.sim')}
                    </th>
                    <th className='whitespace-nowrap px-1.5 py-1 text-right font-semibold'>
                      {t('pricing.kg')}
                    </th>
                    <th className='whitespace-nowrap px-1.5 py-1 text-right font-semibold'>
                      {t('pricing.formula')}
                    </th>
                    <th className='whitespace-nowrap px-1.5 py-1 text-right font-semibold'>
                      {t('pricing.narx135')}
                    </th>
                    <th className='whitespace-nowrap border-r-2 border-border px-1.5 py-1 text-right font-semibold'>
                      {t('pricing.narx')}
                    </th>
                    {sellingTypes.map((st, idx) => (
                      <React.Fragment key={`header-${st.id}`}>
                        <th
                          className={`whitespace-nowrap px-1.5 py-1 text-right font-semibold text-green-700 dark:text-green-400 ${
                            idx % 2 === 0
                              ? 'bg-blue-50/50 dark:bg-blue-950/20'
                              : 'bg-green-50/50 dark:bg-green-950/20'
                          }`}
                        >
                          {t('pricing.foyda')}
                        </th>
                        <th
                          className={`whitespace-nowrap px-1.5 py-1 text-center font-semibold ${
                            idx % 2 === 0
                              ? 'bg-blue-50/50 dark:bg-blue-950/20'
                              : 'bg-green-50/50 dark:bg-green-950/20'
                          }`}
                        >
                          {t('pricing.katak')}
                        </th>
                        <th
                          className={`whitespace-nowrap px-1.5 py-1 text-right font-semibold ${
                            idx % 2 === 0
                              ? 'bg-blue-50/50 dark:bg-blue-950/20'
                              : 'bg-green-50/50 dark:bg-green-950/20'
                          }`}
                        >
                          {t('pricing.sim')}
                        </th>
                        <th
                          className={`whitespace-nowrap border-r border-border px-1.5 py-1 text-right font-semibold text-primary ${
                            idx % 2 === 0
                              ? 'bg-blue-50/50 dark:bg-blue-950/20'
                              : 'bg-green-50/50 dark:bg-green-950/20'
                          }`}
                        >
                          {t('pricing.narx')}
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {wirePrices.map((wp, rowIdx) => {
                    const pricesByType: Record<
                      number,
                      (typeof sellingPrices)[0] | undefined
                    > = {}
                    sellingPrices.forEach((sp) => {
                      if (sp.wire_price.id === wp.id) {
                        pricesByType[sp.selling_type.id] = sp
                      }
                    })

                    // Get group index - count how many unique squares we've seen before this row
                    const groupIndex = wirePrices.slice(0, rowIdx + 1).filter((w, i, arr) =>
                      arr.findIndex(x => x.square === w.square) === i
                    ).length
                    const isOddGroup = groupIndex % 2 === 1

                    return (
                      <tr
                        key={wp.id}
                        className={`border-b border-border hover:bg-muted/50 ${
                          isOddGroup
                            ? 'bg-orange-50 dark:bg-orange-950/20'
                            : 'bg-white dark:bg-background'
                        }`}
                      >
                        <td
                          className='whitespace-nowrap px-1.5 py-1 font-medium'
                          onContextMenu={(e) => {
                            e.preventDefault()
                            setContextMenu({ x: e.clientX, y: e.clientY, wirePrice: wp })
                          }}
                        >
                          {wp.square}
                        </td>
                        <td
                          className='whitespace-nowrap px-1.5 py-1 text-right'
                          onContextMenu={(e) => {
                            e.preventDefault()
                            setContextMenu({ x: e.clientX, y: e.clientY, wirePrice: wp })
                          }}
                        >
                          {wp.thickness_mm}
                        </td>
                        <td
                          className='whitespace-nowrap px-1.5 py-1 text-right'
                          onContextMenu={(e) => {
                            e.preventDefault()
                            setContextMenu({ x: e.clientX, y: e.clientY, wirePrice: wp })
                          }}
                        >
                          <span className='px-1'>{wp.kg.toFixed(2)}</span>
                        </td>
                        <td
                          className='whitespace-nowrap px-1.5 py-1 text-right'
                          onContextMenu={(e) => {
                            e.preventDefault()
                            setContextMenu({ x: e.clientX, y: e.clientY, wirePrice: wp })
                          }}
                        >
                          <EditableCell
                            value={wp.formula}
                            onSave={async (newFormula) => {
                              const updated = await updateWirePrice(wp.id, { formula: newFormula })
                              setWirePrices((prev) =>
                                prev.map((w) => (w.id === wp.id ? updated : w))
                              )
                            }}
                          />
                        </td>
                        <td
                          className='whitespace-nowrap px-1.5 py-1 text-right'
                          onContextMenu={(e) => {
                            e.preventDefault()
                            setContextMenu({ x: e.clientX, y: e.clientY, wirePrice: wp })
                          }}
                        >
                          ${wp.full_price}
                        </td>
                        <td
                          className='whitespace-nowrap border-r-2 border-border px-1.5 py-1 text-right'
                          onContextMenu={(e) => {
                            e.preventDefault()
                            setContextMenu({ x: e.clientX, y: e.clientY, wirePrice: wp })
                          }}
                        >
                          <EditableCell
                            value={wp.price}
                            onSave={async (newPrice) => {
                              const updated = await updateWirePrice(wp.id, {
                                price: newPrice,
                              })
                              setWirePrices((prev) =>
                                prev.map((w) => (w.id === wp.id ? updated : w))
                              )
                              // Refetch selling prices to update calculated values
                              const prices = await fetchSellingPrices()
                              setSellingPrices(prices)
                            }}
                          />
                        </td>
                        {sellingTypes.map((st, idx) => {
                          const sellingPrice = pricesByType[st.id]
                          const bgClass =
                            idx % 2 === 0
                              ? 'bg-blue-50/30 dark:bg-blue-950/10'
                              : 'bg-green-50/30 dark:bg-green-950/10'

                          if (sellingPrice) {
                            return (
                              <React.Fragment key={`${wp.id}-${st.id}`}>
                                <td
                                  className={`whitespace-nowrap px-1.5 py-1 text-right text-green-700 dark:text-green-400 ${bgClass}`}
                                  onContextMenu={(e) => {
                                    e.preventDefault()
                                    setSellingPriceContextMenu({ x: e.clientX, y: e.clientY, sellingPrice })
                                  }}
                                >
                                  <EditableCell
                                    value={sellingPrice.profit}
                                    onSave={async (newProfit) => {
                                      const updated = await updateSellingPrice(
                                        sellingPrice.id,
                                        { profit: newProfit }
                                      )
                                      setSellingPrices((prev) =>
                                        prev.map((sp) =>
                                          sp.id === sellingPrice.id
                                            ? updated
                                            : sp
                                        )
                                      )
                                    }}
                                  />
                                </td>
                                <td
                                  className={`whitespace-nowrap px-1.5 py-1 text-center ${bgClass}`}
                                  onContextMenu={(e) => {
                                    e.preventDefault()
                                    setSellingPriceContextMenu({ x: e.clientX, y: e.clientY, sellingPrice })
                                  }}
                                >
                                  {wp.square}
                                </td>
                                <td
                                  className={`whitespace-nowrap px-1.5 py-1 text-right ${bgClass}`}
                                  onContextMenu={(e) => {
                                    e.preventDefault()
                                    setSellingPriceContextMenu({ x: e.clientX, y: e.clientY, sellingPrice })
                                  }}
                                >
                                  {wp.name}
                                </td>
                                <td
                                  className={`whitespace-nowrap border-r border-border px-1.5 py-1 text-right font-semibold text-primary ${bgClass}`}
                                  onContextMenu={(e) => {
                                    e.preventDefault()
                                    setSellingPriceContextMenu({ x: e.clientX, y: e.clientY, sellingPrice })
                                  }}
                                >
                                  ${sellingPrice.full_price}
                                </td>
                              </React.Fragment>
                            )
                          }
                          return (
                            <React.Fragment key={`${wp.id}-${st.id}`}>
                              <td
                                className={`whitespace-nowrap px-1.5 py-1 text-right text-green-700 dark:text-green-400 ${bgClass}`}
                              >
                                <EditableEmptyCell
                                  onCreate={async (profit) => {
                                    const created = await createSellingPrice({
                                      wire_price: wp.id,
                                      selling_type: st.id,
                                      profit,
                                    })
                                    setSellingPrices((prev) => [...prev, created])
                                  }}
                                />
                              </td>
                              <td
                                className={`whitespace-nowrap px-1.5 py-1 text-center text-muted-foreground ${bgClass}`}
                              >
                                -
                              </td>
                              <td
                                className={`whitespace-nowrap px-1.5 py-1 text-right text-muted-foreground ${bgClass}`}
                              >
                                -
                              </td>
                              <td
                                className={`whitespace-nowrap border-r border-border px-1.5 py-1 text-right text-muted-foreground ${bgClass}`}
                              >
                                -
                              </td>
                            </React.Fragment>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className='mt-2 text-xs text-muted-foreground'>
              {t('pricing.showingCombinations', {
                wires: wirePrices.length,
                types: sellingTypes.length,
                total: wirePrices.length * sellingTypes.length,
                configured: sellingPrices.length
              })}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
